import { format } from '@tusbar/cache-control'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { gql } from '../../../__generated__/graphql/catalog'
import {
  CONTENT_KEY,
  INCLUDE_UNAVAILABLE_PRODUCTS,
  PREVIEW,
  PRODUCT_DATA_KEY,
} from '../../../brunswick/constants'
import { MagnoliaPage, MagnoliaPageInitialProps } from '../../../components/pages/MagnoliaApp'
import {
  PdpDeprecatedServerSidePropsProvider,
  primeClientWithPdpDeprecatedServerSideProps,
} from '../../../components/pages/products/ProductPage/PdpDeprecatedServerSidePropsProvider'
import {
  PdpServerSidePropsProvider,
  primeClientWithPdpServerSideProps,
} from '../../../components/pages/products/ProductPage/PdpServerSidePropsProvider'
import { ProductPage } from '../../../components/pages/products/ProductPage/ProductPage'
import ProductPageMeta from '../../../components/pages/products/ProductPage/ProductPageMeta'
import { TEMPORARY_REDIRECT } from '../../../constants'
import { cachePolicies } from '../../../lib/response-cache'
import { isBrowser } from '../../../lib/utils'
import { singleQueryParamValue } from '../../../lib/utils'

export type ProductPageRouteHandlerProps = MagnoliaPageInitialProps<{
  productHandle: string
  includeUnavailableProducts: boolean
  previewUnpublishedContent: boolean
  launchTags: string | undefined
  color: string | undefined
  fit: string | undefined
  size: string | undefined
}>

const ProductPageRouteHandler: MagnoliaPage<ProductPageRouteHandlerProps> = ({
  productHandle: ssrProductHandle,
  includeUnavailableProducts: ssrPreviewProducts,
  previewUnpublishedContent: ssrPreviewContent,
  launchTags: ssrLaunchTags,
  color: ssrColor,
  fit: ssrFit,
  size: ssrSize,
}) => {
  const clientSearchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined

  const color = clientSearchParams ? clientSearchParams.get('color') ?? undefined : ssrColor
  const fit = clientSearchParams ? clientSearchParams.get('fit') ?? undefined : ssrFit
  const size = clientSearchParams ? clientSearchParams.get('size') ?? undefined : ssrSize

  return (
    <PdpServerSidePropsProvider
      initialState={{
        productHandle: ssrProductHandle,
        colorKey: color,
        fitKey: fit,
        sizeKey: size,
        collectionHandle: undefined,
        includeUnavailable: ssrPreviewProducts,
        isPreview: ssrPreviewContent,
        launchTags: ssrLaunchTags,
      }}
    >
      <PdpDeprecatedServerSidePropsProvider
        initialState={{
          productGroupHandle: ssrProductHandle,
          colorKey: color,
          fitKey: fit,
          collectionHandle: undefined,
          includeUnavailable: ssrPreviewProducts,
          isPreview: ssrPreviewContent,
          launchTags: ssrLaunchTags,
        }}
      >
        <ProductPageMeta handle={ssrProductHandle} />
        <ProductPage />
      </PdpDeprecatedServerSidePropsProvider>
    </PdpServerSidePropsProvider>
  )
}

ProductPageRouteHandler.getInitialProps = async function (pageContext) {
  /**
   * Read data from the request
   */
  const productDataParams = singleQueryParamValue(pageContext.query[PRODUCT_DATA_KEY], '')
  const includeUnavailableProducts = productDataParams === INCLUDE_UNAVAILABLE_PRODUCTS
  const launchTags = includeUnavailableProducts ? '' : productDataParams
  const previewUnpublishedContent = pageContext.query[CONTENT_KEY] === PREVIEW
  // color and fit must default to undefined to avoid passing an empty string to the featured product query:
  const color = singleQueryParamValue(pageContext.query.color, undefined)
  const fit = singleQueryParamValue(pageContext.query.fit, undefined)
  const size = singleQueryParamValue(pageContext.query.size, undefined)
  const productGroupHandle = singleQueryParamValue(pageContext.query.handle, '')
  const cacheBypass =
    singleQueryParamValue(pageContext.query.features, '') === process.env.CACHE_BYPASS_FEATURE
  const explicitNoCache =
    includeUnavailableProducts || previewUnpublishedContent || cacheBypass || !!launchTags?.length

  const pageProps: ProductPageRouteHandlerProps = {
    productHandle: productGroupHandle,
    includeUnavailableProducts,
    previewUnpublishedContent,
    launchTags,
    responseCacheControl: cachePolicies.noCache,
    color,
    fit,
    size,
  }

  /**
   * Render the page immediately by not blocking on getInitialProps and let the
   * page component run a fresh query client-side:
   */
  if (isBrowser()) return pageProps

  /**
   * Prime the Apollo Client cache for SSR
   */
  const client = pageContext.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)

  const validateHandlePromise = client.query({
    errorPolicy: 'all',
    query: gql(`
      query ValidateProductHandle($productHandle: String!) {
        productCatalogStandardProduct(handle: $productHandle) {
          __typename
          id
        }
      }`),
    variables: {
      productHandle: productGroupHandle,
    },
    context: {
      includeUnavailable: includeUnavailableProducts,
      isPreview: previewUnpublishedContent,
      launchTags,
    },
  })

  const pdpServerSidePropsPromise = primeClientWithPdpServerSideProps({
    client,
    props: {
      productHandle: productGroupHandle,
      collectionHandle: undefined,
      colorKey: color,
      fitKey: fit,
      sizeKey: size,
      isPreview: previewUnpublishedContent,
      includeUnavailable: includeUnavailableProducts,
      launchTags,
    },
  })

  const pdpDeprecatedServerSidePropsPromise = primeClientWithPdpDeprecatedServerSideProps({
    client,
    props: {
      productGroupHandle,
      collectionHandle: undefined,
      colorKey: color,
      fitKey: fit,
      isPreview: previewUnpublishedContent,
      includeUnavailable: includeUnavailableProducts,
      launchTags,
    },
  })

  const { data: deprecatedData, errors: pdpDeprecatedServerSidePropsErrors } =
    await pdpDeprecatedServerSidePropsPromise

  const { errors: pdpServerSidePropsErrors } = await pdpServerSidePropsPromise

  /**
   * Combine any errors
   */
  const graphQLErrors = [
    ...(pdpServerSidePropsErrors ?? []),
    ...(pdpDeprecatedServerSidePropsErrors ?? []),
  ]

  /**
   * Handle setting http response codes other than 200
   */
  const notFound =
    graphQLErrors.find(graphQLError => graphQLError.extensions?.errorType === 'NOT_FOUND') ??
    !deprecatedData?.productGroup?.featuredProduct

  if (pageContext.res && notFound) {
    const { data: validateHandleData } = await validateHandlePromise
    if ((color || fit) && validateHandleData?.productCatalogStandardProduct?.id) {
      pageContext.res.writeHead(TEMPORARY_REDIRECT, {
        'Cache-Control': format(cachePolicies.publicHtmlPolicy),
        'Location': pageContext.pathname.replace('[handle]', productGroupHandle),
      })
      pageContext.res.end()
      return pageProps
    } else {
      pageContext.res.statusCode = 404
    }
  }

  return {
    ...pageProps,
    responseCacheControl:
      explicitNoCache || graphQLErrors.length > 0
        ? cachePolicies.noCache
        : cachePolicies.publicHtmlPolicy,
  }
}

export default ProductPageRouteHandler
